import {
  FavoriteSearch,
  getFavoriteSearchesStore,
} from 'features/knowledgeDocumentViewer/utils/favoriteSearchesStorage'
import { useGeckoPermissions } from 'features/permissions'
import { useCallback, useMemo, useSyncExternalStore } from 'react'

const EMPTY: FavoriteSearch[] = []
const NOOP_SUBSCRIBE = () => () => {}
const NOOP_SNAPSHOT = () => EMPTY

export interface UseFavoriteSearchesReturn {
  favoriteSearches: FavoriteSearch[]
  isSearchFavorited: (query: string) => boolean
  toggleSearchFavorite: (query: string, contentFilter: string) => void
}

export function useFavoriteSearches(
  siteId?: string
): UseFavoriteSearchesReturn {
  const { user } = useGeckoPermissions()
  const userId = user?.id

  const store = useMemo(() => {
    if (!userId || !siteId) return null
    return getFavoriteSearchesStore(userId, siteId)
  }, [userId, siteId])

  const favoriteSearches = useSyncExternalStore(
    store?.subscribe ?? NOOP_SUBSCRIBE,
    store?.getStableSnapshot ?? NOOP_SNAPSHOT
  )

  const favoritedQueries = useMemo(
    () => new Set(favoriteSearches.map((f) => f.query.toLowerCase())),
    [favoriteSearches]
  )

  const isSearchFavorited = useCallback(
    (query: string) => favoritedQueries.has(query.toLowerCase()),
    [favoritedQueries]
  )

  const toggleSearchFavorite = useCallback(
    (query: string, contentFilter: string) => {
      if (!store) return
      const current = store.read()
      const exists = current.some(
        (f) => f.query.toLowerCase() === query.toLowerCase()
      )
      if (exists) {
        store.write(
          current.filter((f) => f.query.toLowerCase() !== query.toLowerCase())
        )
      } else {
        store.write([
          { query, contentFilter, timestamp: Date.now() },
          ...current,
        ])
      }
    },
    [store]
  )

  return {
    favoriteSearches,
    isSearchFavorited,
    toggleSearchFavorite,
  }
}
